import React, { useEffect, useState } from "react";
import { Box, Button, Group, Modal, NumberInput, Text } from "@mantine/core";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import surfaceStyles from "../../common/surface/surface.module.css";

interface ChargeAmountModalProps {
  opened: boolean;
  onClose: () => void;
  /** Full balance the method would otherwise charge. */
  fullAmount: number;
  /** Current override (null = full balance). */
  overrideAmount: number | null;
  /** null clears the override (Use full balance). */
  onApply: (amount: number | null) => void;
  /**
   * Permit amounts above the balance. Used for Cash, where the entered
   * amount is the cash received and any overage splits into tip/change.
   */
  allowExceed?: boolean;
  /** Overrides the default explainer copy (e.g. cash-received wording). */
  description?: string;
}

/**
 * "Charge a different amount" — centered modal with a $-amount input,
 * "Use full balance", and "Apply amount". Unless `allowExceed` is set, the
 * amount is capped at the balance (the remainder can be collected
 * separately afterwards).
 */
export default function ChargeAmountModal({
  opened,
  onClose,
  fullAmount,
  overrideAmount,
  onApply,
  allowExceed = false,
  description,
}: ChargeAmountModalProps) {
  const [draft, setDraft] = useState<number | string>("");

  useEffect(() => {
    if (opened) {
      setDraft(overrideAmount ?? "");
    }
  }, [opened, overrideAmount]);

  const parsed = typeof draft === "number" ? draft : parseFloat(draft);
  const isValid =
    !Number.isNaN(parsed) &&
    parsed > 0 &&
    (allowExceed || parsed <= fullAmount);

  const handleApply = () => {
    if (!isValid) return;
    const rounded = Math.round(parsed * 100) / 100;
    // Applying the exact full balance is the same as clearing the override.
    onApply(rounded === Math.round(fullAmount * 100) / 100 ? null : rounded);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Charge a different amount"
      size={380}
      radius={18}
      centered
    >
      <Text size="sm" c="dimmed" mb="md" lh={1.5}>
        {description ??
          "Enter the amount to charge this payment method. The remaining balance can be collected separately."}
      </Text>

      <NumberInput
        value={draft}
        onChange={setDraft}
        placeholder={formatCurrency(fullAmount).replace("$", "")}
        prefix="$"
        min={0.01}
        max={allowExceed ? undefined : fullAmount}
        decimalScale={2}
        size="lg"
        hideControls
        data-autofocus
      />

      <Group justify="space-between" mt={12}>
        <Text size="xs" c="dimmed">
          Full balance {formatCurrency(fullAmount)}
        </Text>
        <Button
          variant="subtle"
          size="compact-xs"
          color="var(--brand-teal)"
          onClick={() => {
            onApply(null);
            onClose();
          }}
        >
          Use full balance
        </Button>
      </Group>

      <Box mt={18}>
        <Button
          fullWidth
          classNames={{ root: surfaceStyles.primaryAction }}
          onClick={handleApply}
          disabled={!isValid}
        >
          Apply amount
        </Button>
      </Box>
    </Modal>
  );
}
